<template>
    <div :class="`grid-container-content bg-primary-background ${noSpacing}`">
        <div class="col-span-full grid-container-content content-gutters w-full">
            <!-- Free Text Header -->
            <h1
                v-if="header"
                class="heading-brand-base col-span-full lg:col-start-2 md:col-span-6 text-left mb-10 p-no-margin"
                v-html="header"
            />

            <!-- Free Sub Text Header -->
            <div
                v-if="subText"
                class="col-span-full lg:col-start-2 md:col-span-7 mb-10"
                v-html="subText"
            />

            <!-- Filters -->
            <facilities-filters
                v-if="filters.length > 0"
                :filters="filters"
                class="col-span-full lg:col-start-2 lg:col-span-10 mb-16"
                @filters-updated="filtersUpdated"
            />
        </div>

        <!-- LLC MPC Map -->
        <div ref="llcMpcMapContainer" class="col-span-full">
            <transition-group name="fade">
                <div v-if="!listView" class="md:content-gutters">
                    <llc-mpc-map
                        key="map"
                        :community-data="communityData"
                        :hits="hits"
                        :map-type="mapType"
                        :is-loading="isLoading"
                        :show-list-button="mapType === 'amenity'"
                        @show-list-view="showListView"
                    />
                </div>
                <div v-if="listView" key="list" class="content-gutters">
                    <amenity-list
                        v-if="mapType === 'amenity'"
                        :hits="hits"
                        list-type="Amenity"
                        :is-loading="isLoading"
                    />
                    <div class="flex justify-center mt-6 md:mt-8">
                        <stockland-button
                            additional-button-classes="mx-auto"
                            label="Show map view"
                            :is-visible="true"
                            :is-loading="false"
                            type="button"
                            layout="icon-left"
                            variant="style-2"
                            size="small"
                            icon="location"
                            @button-clicked="(showMapView)"
                        />
                    </div>
                </div>
            </transition-group>
        </div>
        <!-- We need Property List and Community List here -->
    </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, toRefs } from 'vue';

import { generateUrlWithQuery } from '@root/src/framework/scripts/utils/url';

import { useApiGet } from '../../../framework/scripts/use/api';
import { scrollIntoView } from '../../../framework/scripts/utils/scroll';
import { Filter, Item } from '../facilities-filters/interfaces';

import { LlcMpcMapProperties, LlcMpcResponse,PropertyMapState, RequestParameters } from './interfaces';

const useLocationMap = (props: LlcMpcMapProperties) => {
    const { data, isLoading, getData } = useApiGet<LlcMpcResponse>();
    const state: PropertyMapState = reactive({
        activeFilter: null,
        activeSubFilter: null,
        communityData: computed(() => {
            return data.value?.communityData ?? null;
        }),
        filters: computed(() => {
            return data.value?.filters ?? [];
        }),
        hits: computed(() => {
            return data.value?.hits ?? [];
        }),
        isLoading,
        listView: false,
        llcMpcMapContainer: null,
        loaded: false,
        mapType: props.mapType,
        isFullscreen: false,
        propertyCard: null,
        noSpacing: props.noComponentSpacing === true ? '': 'component-spacing'
    });

    onMounted(() => {
        getData(generateUrlWithQuery(props.apiUrl, queryObject()));
        state.loaded = true; // this might not be needed because we have a watcher on markers
    });

    const queryObject = () => {
        let parameters: RequestParameters = {
            page: 1,
            pageSize: 1000,
            type: state.mapType,
            filters: {},
        };
        if(state.mapType === "amenity") {
            parameters.type = "Amenity";
        }
        if (props.displayType) {
            parameters.type = props.displayType;
        }
        if(props.communityId) {
            parameters.communityId = props.communityId;
        }
        if(state.activeFilter) {
            parameters.filters.primary = state.activeFilter.label;
        }
        if(state.activeSubFilter) {
            parameters.filters.secondary = state.activeSubFilter.label;
        }
        return parameters;
    }

    const filtersUpdated = (primary: Filter | null, secondary: Item | null) => {
        state.activeFilter = primary;
        state.activeSubFilter = secondary;
        const parameters = generateUrlWithQuery(props.apiUrl, queryObject());
        getData(parameters);
    }

    function showListView() {
        scrollIntoView(state.llcMpcMapContainer as HTMLElement, {
            behavior: 'instant',
            block: 'start',
            inline: 'start',
        });
        state.listView = true;
    }

    function showMapView() {
        scrollIntoView(state.llcMpcMapContainer as HTMLElement, {
            behavior: 'instant',
            block: 'start',
            inline: 'start',
        });
        state.listView = false;
    }

    return {
        state,
        filtersUpdated,
        showListView,
        showMapView,
    };
}

export default defineComponent({
    name: 'llc-mpc-map-container',
    props: {
        communityId: {
            type: String,
            default: null
        },
        header: {
            type: String,
            default: ''
        },
        subText: {
            type: String,
            default: ''
        },
        mapType: {
            type: String,
            default: ''
        },
        apiUrl: {
            type: String,
            default: ''
        },
        displayType: {
            type: String,
            default: ''
        },
        noComponentSpacing: {
            type: Boolean,
            required: false
        }
    },
    setup(props: LlcMpcMapProperties) {
        const { state, ...methods } = useLocationMap(props);
        return {
            ...toRefs(state),
            ...methods
        };

    },
});
</script>
<style lang="postcss"></style>
